import React from 'react';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import tw, { css } from 'twin.macro';
import { ArrowIcon } from './Icons';
import ContentLabel from './ContentLabel';
import DateLabel from './DateLabel';
import { createDescription, isOldTowardsNo1Post } from '../utils';

const AnimateIn = css`
  li {
    animation: 1s animate forwards;
  }
  li:nth-of-type(1) {
    animation-delay: 150ms;
  }
  li:nth-of-type(2) {
    animation-delay: 300ms;
  }
  li:nth-of-type(3) {
    animation-delay: 450ms;
  }
  li:nth-of-type(4) {
    animation-delay: 600ms;
  }
  li:nth-of-type(5) {
    animation-delay: 750ms;
  }
  li:nth-of-type(6) {
    animation-delay: 900ms;
  }
  li:nth-of-type(7) {
    animation-delay: 1050ms;
  }
  li:nth-of-type(8) {
    animation-delay: 1200ms;
  }
  li:nth-of-type(9) {
    animation-delay: 1350ms;
  }
  li:nth-of-type(10) {
    animation-delay: 1500ms;
  }
  @keyframes animate {
    0% {
      transform: scale(0.9) translateY(-20px);
      opacity: 0;
    }
    100% {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }
`;

const gridStyles = css`
  ${tw`relative w-full flex flex-wrap justify-between text-white`} ::after {
    content: '';
    flex: auto;
  }
`;

const cardStyles = css`
  transform-origin: top left;
  ${tw`relative overflow-hidden mb-12 md:mb-20 opacity-0`}
  margin: 0 10px;
  width: 100%;
  @media screen and (min-width: 640px) {
    width: 31%;
    &:nth-of-type(1),
    &:nth-of-type(2),
    &:nth-of-type(6),
    &:nth-of-type(7) {
      width: 48%;
    }
  }
  &:nth-of-type(1),
  &:nth-of-type(3),
  &:nth-of-type(6),
  &:nth-of-type(8) {
    margin-left: 0;
  }
  &:nth-of-type(2),
  &:nth-of-type(5),
  &:nth-of-type(7),
  &:nth-of-type(10) {
    margin-right: 0;
  }
  @media screen and (max-width: 640px) {
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 2.5rem;
  }
  img {
    color: transparent;
    transform: scale(1);
    transition: transform 500ms !important;
  }
  &:hover img {
    transform: scale(1.05);
  }
  svg {
    ${tw`transform origin-left scale-100 duration-500`}
  }
  &:hover svg {
    ${tw`scale-150`}
  }
`;

const bigCardStyles = css`
  @media screen and (min-width: 640px) {
    height: 450px;
  }
`;

const imgStyles = css`
  height: 280px;
`;

const Posts = ({ posts, idx }) => (
  <ul id={`posts-list-${idx}`} css={[AnimateIn, gridStyles]}>
    {posts.map((post, i) => (
      <Card {...post} key={post.slug} idx={i + 1} />
    ))}
  </ul>
);

const Card = ({
  featuredMedia,
  date,
  dateTime,
  sortDate,
  content,
  contentNode: { childMarkdownRemark },
  slug,
  title,
  model,
  idx,
  expert,
}) => {
  if (!featuredMedia && !expert?.techTalentImage) return null;
  const image = featuredMedia || expert.techTalentImage;
  const bigCard = idx === 1 || idx === 2 || idx === 6 || idx === 7;

  return (
    <li css={cardStyles}>
      <Link to={`/${slug}`} state={{ postType: model.name }}>
        <ContentLabel label={model?.name} inset />
        <GatsbyImage
          image={image.gatsbyImageData}
          css={[imgStyles, bigCard && bigCardStyles]}
          alt={title}
        />
        <div tw="px-4 py-3 md:p-5">
          <DateLabel dateTime={dateTime} date={date} />
          <p tw="font-semibold mb-2">{title}</p>
          {isOldTowardsNo1Post(model.name, sortDate) ? (
            <p tw="text-base">{createDescription(content)}</p>
          ) : (
            <p tw="text-base">{childMarkdownRemark.excerpt}</p>
          )}
          <div tw="flex items-center absolute bottom-0">
            Read{' '}
            <span tw="ml-5">
              <ArrowIcon />
            </span>
          </div>
        </div>
      </Link>
    </li>
  );
};

export default Posts;
